/**
 * @module features/clients/components/ClientTable
 * @description Desktop & tablet data table adhering to the application design system (UserTable & DeviceTable).
 * Features rounded-xl container, h-[52px] rows, clickable rows for instant slide-over inspector,
 * custom monogram avatars, semantic badges, and stopPropagation quick action buttons.
 */
import * as React from "react";
import {
  Eye,
  Edit,
  UserPlus,
  ShieldAlert,
  Trash2,
  MapPin,
  Copy,
  Check,
} from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ClientSummary } from "../types";
import { ClientQuotaGauge } from "./ClientQuotaGauge";

export interface ClientTableProps {
  clients: ClientSummary[];
  onView: (client: ClientSummary) => void;
  onEdit: (client: ClientSummary) => void;
  onLinkUsers: (client: ClientSummary) => void;
  onToggleStatus: (client: ClientSummary) => void;
  onDelete: (client: ClientSummary) => void;
}

export function ClientTable({
  clients,
  onView,
  onEdit,
  onLinkUsers,
  onToggleStatus,
  onDelete,
}: ClientTableProps) {
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const handleCopyId = (id: string) => {
    try {
      navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => {
        setCopiedId((curr) => (curr === id ? null : curr));
      }, 2000);
    } catch {
      // Ignore
    }
  };

  return (
    <div className="rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30 border-b border-border/80">
            <TableHead className="w-[340px] text-xs font-medium">Organization</TableHead>
            <TableHead className="text-xs font-medium">Status</TableHead>
            <TableHead className="hidden lg:table-cell text-xs font-medium">Theme</TableHead>
            <TableHead className="text-xs font-medium">Seat Capacity</TableHead>
            <TableHead className="hidden md:table-cell text-xs font-medium">Contact Person</TableHead>
            <TableHead className="hidden xl:table-cell text-xs font-medium">Location</TableHead>
            <TableHead className="text-right text-xs font-medium">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                className="h-32 text-center text-muted-foreground text-xs"
              >
                No client organizations matched your filter criteria.
              </TableCell>
            </TableRow>
          ) : (
            clients.map((client) => {
              const statusPill =
                client.status === "active"
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                  : client.status === "suspended"
                  ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                  : "bg-muted text-muted-foreground border-border";

              return (
                <TableRow
                  key={client.id}
                  onClick={() => onView(client)}
                  className="h-[52px] hover:bg-muted/20 cursor-pointer transition-colors border-b border-border/60"
                >
                  {/* Organization Profile Cell */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {client.logo_url ? (
                        <img
                          src={client.logo_url}
                          alt={`${client.org_name} logo`}
                          className="h-9 w-9 rounded-xl object-contain border border-border/80 bg-white p-1 shrink-0 shadow-xs"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="h-9 w-9 rounded-xl border border-border/80 flex items-center justify-center font-bold text-xs shrink-0 shadow-xs"
                          style={{
                            backgroundColor: `${client.theme_color}18`,
                            color: client.theme_color,
                          }}
                        >
                          {client.org_name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 space-y-0.5">
                        <p className="font-semibold text-sm text-foreground truncate">
                          {client.org_name}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {client.org_email_id}
                        </p>
                        <div className="flex items-center gap-1.5 pt-0.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyId(client.id);
                            }}
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground bg-muted/60 hover:bg-muted px-2 py-0.5 rounded border border-border/70 transition-colors group select-all shadow-2xs"
                            title="Click to copy Client Organization ID"
                          >
                            <span className="font-semibold text-foreground">ID:</span>
                            <span className="font-mono tracking-tight">{client.id}</span>
                            {copiedId === client.id ? (
                              <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                            ) : (
                              <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100 shrink-0" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </TableCell>

                  {/* Status Badge with semantic indicator */}
                  <TableCell>
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-medium px-2 py-0.5 rounded-full border capitalize ${statusPill}`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          client.status === "active"
                            ? "bg-emerald-500"
                            : client.status === "suspended"
                            ? "bg-amber-500"
                            : "bg-zinc-400"
                        }`}
                      />
                      {client.status}
                    </span>
                  </TableCell>

                  {/* Brand Theme Color */}
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-3.5 w-3.5 rounded-full ring-1 ring-border/80 shrink-0 shadow-xs"
                        style={{ backgroundColor: client.theme_color }}
                      />
                      <span className="font-mono text-xs text-muted-foreground uppercase">
                        {client.theme_color}
                      </span>
                    </div>
                  </TableCell>

                  {/* Seat Capacity Gauge */}
                  <TableCell>
                    <ClientQuotaGauge
                      activeCount={client.active_user_count}
                      maxLimit={client.max_user_limit}
                      compact
                    />
                  </TableCell>

                  {/* Contact Person */}
                  <TableCell className="hidden md:table-cell">
                    <div className="text-xs space-y-0.5">
                      <p className="font-medium text-foreground truncate max-w-[160px]">
                        {client.contact_person_name}
                      </p>
                      <p className="text-muted-foreground font-mono truncate max-w-[160px]">
                        {client.org_contact_no}
                      </p>
                    </div>
                  </TableCell>

                  {/* Location */}
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0 opacity-70" />
                      <span className="truncate max-w-[140px]">
                        {client.city}, {client.state}
                      </span>
                    </div>
                  </TableCell>

                  {/* Quick Actions (stop propagation to keep row click for inspector) */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(client)}
                        aria-label={`Inspect ${client.org_name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        title="View organization details"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(client)}
                        aria-label={`Edit ${client.org_name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        title="Edit organization profile"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onLinkUsers(client)}
                        aria-label={`Link users to ${client.org_name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        title="Link users"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleStatus(client)}
                        aria-label={`Toggle status for ${client.org_name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400"
                        title={client.status === "active" ? "Suspend organization" : "Reactivate organization"}
                      >
                        <ShieldAlert className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(client)}
                        aria-label={`Delete ${client.org_name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        title="Deactivate organization"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
